import { assertIsNotUndefined } from '@gitbook/util-ts';

import {
    COLLECTION_TYPE_SYMBOL,
    type Collection,
    type CollectionEntry,
    type CollectionIterateFn,
    type CollectionKey,
    isCollection,
} from '../collection';
import { equals } from '../equals';
import { freezeCollection, hashCollection } from '../hash';
import { type DeepReadonly, deepFreeze, isPrimitive } from '../utils';

export type { DeepReadonly };
export type ImmutableMapKey = CollectionKey;

export type ImmutableMapInit<Key extends ImmutableMapKey, Value> =
    | Array<[Key, Value]>
    | Record<Key, Value>
    | Map<Key, Value>
    | Collection<Key, Value>
    | Record<string, never>;

const FROZEN_OBJECT_CACHE = new WeakMap<ImmutableMap<any, any>, DeepReadonly<Record<any, any>>>();

let emptyMap: ImmutableMap<any, any> | undefined;

export const MAP_TYPE_SYMBOL = Symbol('map');

/**
 * Immutable Map is an unordered collection of (key, value) pairs.
 */
export class ImmutableMap<Key extends ImmutableMapKey, Value> implements Collection<Key, Value> {
    public static isKeyed = true;
    public static isOrdered = false;

    public readonly size: number;
    protected readonly _map: Map<Key, Value>;

    constructor(entries: Map<Key, Value>, constructorTypeCheck: typeof MAP_TYPE_SYMBOL) {
        if (constructorTypeCheck !== MAP_TYPE_SYMBOL) {
            throw new Error(
                'ImmutableMap constructor is private, use ImmutableMap.create() instead'
            );
        }

        this._map = entries;
        this.size = this._map.size;
    }

    /**
     * Create a list from a set of values.
     * This function is optimized to return the same instance when initializing with an empty array.
     */
    public static create<Key extends ImmutableMapKey, Value>(
        entries?: ImmutableMapInit<Key, Value>
    ): ImmutableMap<Key, Value> {
        if (ImmutableMap.isMap(entries)) {
            return entries;
        }

        const input = initializeNativeMap(entries);

        if (input.size === 0) {
            emptyMap = emptyMap || freezeCollection(new ImmutableMap(input, MAP_TYPE_SYMBOL));
            return emptyMap;
        }

        return freezeCollection(new ImmutableMap(input, MAP_TYPE_SYMBOL));
    }

    public static isMap(maybeMap: any): maybeMap is ImmutableMap<any, any> {
        return isCollection(maybeMap, MAP_TYPE_SYMBOL);
    }

    /**
     * Returns true if this Map includes no values.
     */
    public isEmpty(): boolean {
        return this._map.size === 0;
    }

    public has(key: Key): boolean {
        return this._map.has(key);
    }

    /**
     * Returns the value associated with the provided key, or notSetValue if the Map does not contain this key.
     */
    public get(key: Key, notSetValue?: Value): Value | undefined {
        if (notSetValue !== undefined && !this.has(key)) {
            return notSetValue;
        }

        return this._map.get(key);
    }

    public getByIndex(_index: number): CollectionEntry<Key, Value> | undefined {
        throw new Error('Maps do not support getByIndex');
    }

    public getByKey(key: Key): CollectionEntry<Key, Value> | undefined {
        if (!this.has(key)) {
            return undefined;
        }
        // @ts-ignore
        return [key, this._map.get(key)];
    }

    /**
     * Compare the value of this Map to the value of another Map.
     */
    public equals(other: any): boolean {
        if (this === other) {
            return true;
        }

        if (this.constructor !== other.constructor || this.size !== other.size) {
            return false;
        }

        if (hashCollection(this) !== hashCollection(other)) {
            return false;
        }

        return this.every((value, key) => {
            return equals(value, other.get(key));
        });
    }

    /**
     * Returns the value associated with a path in a tree of nested Maps.
     */
    public getIn(path: ImmutableMapKey[]): any {
        if (path.length === 0) {
            throw new Error('getIn expect a path with at least one entry');
        }

        let result: any = this;
        for (const part of path) {
            if (isPrimitive(result)) {
                return undefined;
            }
            if (result instanceof ImmutableMap || result instanceof Map) {
                result = result.get(part);
            } else {
                result = result[part];
            }
        }

        return result;
    }

    /**
     * Returns a new Map having set value at this keyPath.
     * If any keys in keyPath do not exist, a new JS object will be created at that key.
     */
    public setIn(keyPath: [Key, ...Array<ImmutableMapKey>], value: any): this {
        return this.update(setInJSMap(this._map, keyPath, () => value));
    }

    /**
     * Returns a new Map having removed the value at this keyPath.
     * If any keys in keyPath do not exist, no change will occur.
     */
    public deleteIn(keyPath: [Key, ...Array<ImmutableMapKey>]): this {
        if (keyPath.length === 1) {
            const key = keyPath[0];
            return this.delete(key);
        }

        const key = keyPath[keyPath.length - 1]!;
        const parent = keyPath.slice(0, -1);

        return this.update(
            setInJSMap(this._map, parent, (map) => {
                if (ImmutableMap.isMap(map)) {
                    return map.delete(key);
                }
                if (map instanceof Map) {
                    const newMap = new Map(map);
                    newMap.delete(key);
                    return newMap;
                }
                const { [key]: _, ...copy } = map;
                return copy;
            })
        );
    }

    /**
     * Returns a new Map also containing the new key, value pair.
     * If an equivalent key already exists in this Map, it will be replaced.
     */
    public set(key: Key, value: Value): this {
        return this.withMutations((map) => {
            if (map.has(key) && equals(map.get(key), value)) {
                return false;
            }
            map.set(key, value);
        });
    }

    /**
     * Set multiple values at once.
     */
    public merge(other: Record<Key, Value> | Collection<Key, Value>): this {
        return this.withMutations((map) => {
            if (isCollection(other)) {
                const entries = other.toEntriesArray();
                entries.forEach(([key, value]) => {
                    map.set(key, value);
                });
            } else {
                const keys = Object.keys(other);

                /**
                 * Assert that the given key is a key of the passed argument.
                 * This is to get around Object.keys losing type information.
                 */
                function assertIsKey(k: string | number | symbol): asserts k is Key {
                    if (!(k in other)) {
                        throw new Error(`unknown key ${String(k)} from ${keys.join(', ')}`);
                    }
                }

                keys.forEach((key) => {
                    assertIsKey(key);
                    map.set(key, other[key]);
                });
            }
        });
    }

    /**
     * Returns a new Map which excludes this `key`.
     */
    public delete(key: Key): this {
        return this.withMutations((map) => map.delete(key));
    }

    /**
     * The sideEffect is executed for every entry in the Map.
     */
    public forEach(fn: (value: Value, key: Key, collection: this) => unknown): number {
        let iterations = 0;

        for (const [key, value] of this._map) {
            if (fn(value, key, this) === false) {
                break;
            }
            iterations++;
        }

        return iterations;
    }

    /**
     * Returns a new Map with values passed through a `mapper` function.
     */
    public map(mapper: (value: Value, key: Key, collection: this) => Value): this {
        return this.withMutations((map) => {
            for (const [key, value] of map) {
                map.set(key, mapper(value, key, this));
            }
        });
    }

    /**
     * Return a new map with only the value for which the predicate returns true.
     */
    public filter(fn: (value: Value, key: Key, collection: this) => boolean): this {
        return this.withMutations((map) => {
            for (const [key, value] of map) {
                if (!fn(value, key, this)) {
                    map.delete(key);
                }
            }
        });
    }

    /**
     * True if predicate returns true for any entry in the Map.
     */
    public some(fn: (value: Value, key: Key, collection: this) => boolean): boolean {
        for (const [key, value] of this._map) {
            if (fn(value, key, this)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Executes a user-supplied "reducer" callback function on each element of the collection, in order, passing in the return value from the calculation on the preceding element.
     * The final result of running the reducer across all elements of the collection is a single value.
     */
    public reduce<U>(
        callback: (previousValue: U, currentValue: Value, currentKey: Key, map: this) => U,
        initialValue: U
    ): U {
        return this.toArray().reduce(
            (previousValue, [currentKey, currentValue]) =>
                callback(previousValue, currentValue, currentKey, this),
            initialValue
        );
    }

    /**
     * True if predicate returns true for all entries in the Map.
     */
    public every(
        fn: (value: Value, key: Key, array: ImmutableMap<Key, Value>) => boolean
    ): boolean {
        for (const [key, value] of this._map) {
            if (!fn(value, key, this)) {
                return false;
            }
        }

        return true;
    }

    /**
     * Returns a new OrderedMap of the same type with other values and collection-like concatenated to this one.
     */
    public concat(...collections: Array<ImmutableMap<Key, Value>>): this {
        return freezeCollection(
            makeMap(this, new Map(this.toArray().concat(...collections.map((c) => c.toArray()))))
        );
    }

    /**
     * Replace the entire map by new entries.
     */
    public update(entries: Map<Key, Value>): this {
        return freezeCollection(makeMap(this, entries));
    }

    /**
     * Return a native map with the same values.
     */
    public toMap(): Map<Key, Value> {
        return new Map(this._map);
    }

    /**
     * Shallowly converts this collection to an Array.
     */
    public toArray(): Array<[Key, Value]> {
        return this.toEntriesArray();
    }

    /**
     * An array of this Map's keys.
     */
    public toKeysArray(): Array<Key> {
        return Array.from(this._map.keys());
    }

    /**
     * An array of this Map's values.
     */
    public toValuesArray(): Array<Value> {
        return Array.from(this._map.values());
    }

    /**
     * Shallowly converts this collection to an Array of all entries
     */
    public toEntriesArray(): Array<[Key, Value]> {
        return Array.from(this._map);
    }

    /**
     * Shallowly converts this Map to an Object.
     */
    public toObject<T = Record<Key, Value>>(): T {
        // @ts-ignore
        const obj: T = {};
        this.forEach((value, key) => {
            // @ts-ignore
            obj[key] = value;
        });
        return obj;
    }

    /**
     * Shallowly converts this Map to a frozen Object.
     * The object is cached to avoid creating a new object every time.
     */
    public toFrozenObject<T extends Record<Key, Value>>(): DeepReadonly<T> {
        if (FROZEN_OBJECT_CACHE.has(this)) {
            const value = FROZEN_OBJECT_CACHE.get(this);
            assertIsNotUndefined(value);

            /**
             * As FROZEN_OBJECT_CACHE is defined outside of the function, we lose
             * the type info on the generic.
             */
            function assertIsT(v: unknown): asserts v is DeepReadonly<T> {
                if (v !== value) {
                    throw new Error('expected frozen object cache to return same value');
                }
            }

            assertIsT(value);
            return value;
        }

        const object = this.toObject<T>();
        const frozen = deepFreeze(object);
        FROZEN_OBJECT_CACHE.set(this, frozen);
        return frozen;
    }

    /**
     * True if `iter` includes every value in this Collection.
     */
    public isSuperset(iter: ImmutableMapInit<Key, Value>): boolean {
        const iterMap = ImmutableMap.create(iter);
        return iterMap.every((value, key) => {
            return value === this.get(key);
        });
    }

    /**
     * Execute a function on a mutable version of the map.
     */
    public withMutations(change: (mutableMap: Map<Key, Value>) => any): this {
        const copy = this.toMap();
        if (change(copy) === false) {
            return this;
        }
        return this.update(copy);
    }

    /**
     * Super-set of the collection iterate.
     */
    public iterate(fn: CollectionIterateFn<Key, Value>): void {
        this._map.forEach((value, key) => fn(value, key));
    }

    public hashCode(): number {
        return hashCollection(this);
    }
}

/**
 * For fast type-checking.
 * We store it on the prototype so that it's not enumerable and will be copied over by makeMap.
 */
// @ts-ignore
ImmutableMap.prototype[COLLECTION_TYPE_SYMBOL] = MAP_TYPE_SYMBOL;

/**
 * Initialize a native map.
 */
export function initializeNativeMap<Key extends ImmutableMapKey, Value>(
    entries?: ImmutableMapInit<Key, Value>
): Map<Key, Value> {
    if (!entries) {
        return new Map();
    }
    if (entries instanceof Map) {
        return entries;
    }
    if (isCollection(entries)) {
        return new Map(entries.toEntriesArray());
    }
    if (Array.isArray(entries)) {
        return new Map(entries);
    }
    if (!entries) {
        return new Map();
    }
    // @ts-ignore
    return new Map(Object.entries(entries));
}

function setInJSMap<
    M extends
        | Map<ImmutableMapKey, any>
        | ImmutableMap<ImmutableMapKey, any>
        | { [key: ImmutableMapKey]: any },
>(map: M, path: ImmutableMapKey[], transform: (prev: any) => any): M {
    const [key, ...rest] = path;

    // @ts-ignore
    const current = (ImmutableMap.isMap(map) || map instanceof Map ? map.get(key) : map[key]) || {};

    let setValue;
    if (rest.length === 0) {
        setValue = transform(current);
    } else {
        setValue = setInJSMap(current, rest, transform);
    }

    if (ImmutableMap.isMap(map)) {
        // @ts-ignore
        return map.set(key, setValue);
    }
    if (map instanceof Map) {
        const newMap = new Map(map);
        newMap.set(key, setValue);
        // @ts-ignore
        return newMap;
    }

    // @ts-ignore
    return { ...map, [key]: setValue };
}

/**
 * Fast constructor for new list entries as "new X" is slower.
 */
function makeMap<K extends ImmutableMapKey, V, T extends ImmutableMap<K, V>>(
    likeMap: T,
    map: Map<K, V>
): T {
    const record = Object.create(Object.getPrototypeOf(likeMap));
    record._map = map;
    record.size = map.size;
    return record;
}
